/**
 * Station Log API Service
 */

import {
  StationLogEntry,
  StationLogEntrySummary,
  CreateStationLogEntry,
  ShiftHandover,
  CreateShiftHandover,
  StationLogFilters,
  ShiftHandoverFilters,
  StationLogSummary,
  PaginatedStationLogResponse,
  PaginatedShiftHandoverResponse,
} from '../types/stationLog';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

class StationLogService {
  private baseURL: string;

  constructor() {
    this.baseURL = `${API_BASE_URL}/station-log`;
  }

  // Station Log Entries
  async getEntries(filters: StationLogFilters = {}, page = 1): Promise<PaginatedStationLogResponse> {
    const params = new URLSearchParams();
    
    // Add filters to params
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
    
    // Add pagination
    if (page > 1) {
      params.append('page', String(page));
    }
    
    const queryString = params.toString();
    const url = `${this.baseURL}/entries/${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch entries' }));
      throw new Error(errorData.detail || 'Failed to fetch station log entries');
    }

    return response.json();
  }

  async getEntry(id: number): Promise<StationLogEntry> {
    const response = await fetch(`${this.baseURL}/entries/${id}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch entry' }));
      throw new Error(errorData.detail || 'Failed to fetch station log entry');
    }

    return response.json();
  }

  async createEntry(entryData: CreateStationLogEntry): Promise<StationLogEntry> {
    const response = await fetch(`${this.baseURL}/entries/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(entryData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to create entry' }));
      throw new Error(errorData.detail || 'Failed to create station log entry');
    }

    return response.json();
  }

  async updateEntry(id: number, entryData: Partial<CreateStationLogEntry>): Promise<StationLogEntry> {
    const response = await fetch(`${this.baseURL}/entries/${id}/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(entryData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to update entry' }));
      throw new Error(errorData.detail || 'Failed to update station log entry');
    }

    return response.json();
  }

  async deleteEntry(id: number): Promise<void> {
    const response = await fetch(`${this.baseURL}/entries/${id}/`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to delete entry' }));
      throw new Error(errorData.detail || 'Failed to delete station log entry');
    }
  }

  // Station Log Actions
  async acknowledgeEntry(id: number): Promise<StationLogEntry> {
    const response = await fetch(`${this.baseURL}/entries/${id}/acknowledge/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to acknowledge entry' }));
      throw new Error(errorData.detail || 'Failed to acknowledge station log entry');
    }

    return response.json();
  }

  async completeAction(id: number): Promise<StationLogEntry> {
    const response = await fetch(`${this.baseURL}/entries/${id}/complete_action/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to complete action' }));
      throw new Error(errorData.detail || 'Failed to complete action');
    }

    return response.json();
  }

  // Summary and Reports
  async getSummary(filters: StationLogFilters = {}): Promise<StationLogSummary> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
    
    const queryString = params.toString();
    const url = `${this.baseURL}/entries/summary/${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch summary' }));
      throw new Error(errorData.detail || 'Failed to fetch station log summary');
    }

    return response.json();
  }

  async getRecentEntries(): Promise<StationLogEntrySummary[]> {
    const response = await fetch(`${this.baseURL}/entries/recent/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch recent entries' }));
      throw new Error(errorData.detail || 'Failed to fetch recent entries');
    }

    return response.json();
  }

  async getTodayEntries(): Promise<StationLogEntrySummary[]> {
    const response = await fetch(`${this.baseURL}/entries/today/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch today entries' }));
      throw new Error(errorData.detail || 'Failed to fetch today\'s entries');
    }

    return response.json();
  }

  async getPendingActions(): Promise<StationLogEntry[]> {
    const response = await fetch(`${this.baseURL}/entries/pending_actions/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch pending actions' }));
      throw new Error(errorData.detail || 'Failed to fetch pending actions');
    }

    return response.json();
  }

  // Shift Handovers
  async getHandovers(filters: ShiftHandoverFilters = {}, page = 1): Promise<PaginatedShiftHandoverResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
    
    if (page > 1) {
      params.append('page', String(page));
    }
    
    const queryString = params.toString();
    const url = `${this.baseURL}/handovers/${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch handovers' }));
      throw new Error(errorData.detail || 'Failed to fetch shift handovers');
    }

    return response.json();
  }

  async getHandover(id: number): Promise<ShiftHandover> {
    const response = await fetch(`${this.baseURL}/handovers/${id}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch handover' }));
      throw new Error(errorData.detail || 'Failed to fetch shift handover');
    }

    return response.json();
  }

  async createHandover(handoverData: CreateShiftHandover): Promise<ShiftHandover> {
    const response = await fetch(`${this.baseURL}/handovers/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(handoverData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to create handover' }));
      throw new Error(errorData.detail || 'Failed to create shift handover');
    }

    return response.json();
  }

  async acknowledgeHandover(id: number): Promise<ShiftHandover> {
    const response = await fetch(`${this.baseURL}/handovers/${id}/acknowledge/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to acknowledge handover' }));
      throw new Error(errorData.detail || 'Failed to acknowledge shift handover');
    }

    return response.json();
  }

  async getCurrentShift(): Promise<ShiftHandover> {
    const response = await fetch(`${this.baseURL}/handovers/current_shift/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'No current shift found' }));
      throw new Error(errorData.detail || 'Failed to fetch current shift');
    }

    return response.json();
  }
}

// Export singleton instance
export const stationLogService = new StationLogService();
export default stationLogService; 